import React from "react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-json";
import "prismjs/components/prism-python";
import "prismjs/themes/prism-tomorrow.css";
import { FileCode, FileCss, FileJs, FileHtml, File } from "@phosphor-icons/react";

const prismLang = (lang) => {
  const map = { html: "markup", css: "css", javascript: "javascript", jsx: "jsx", json: "json", python: "python" };
  return Prism.languages[map[lang]] ? map[lang] : "markup";
};

const iconFor = (name) => {
  if (name.endsWith(".html")) return <FileHtml size={14} className="text-orange-400" />;
  if (name.endsWith(".css")) return <FileCss size={14} className="text-blue-400" />;
  if (name.endsWith(".js") || name.endsWith(".jsx")) return <FileJs size={14} className="text-yellow-400" />;
  if (name.endsWith(".json")) return <FileCode size={14} className="text-green-400" />;
  return <File size={14} className="text-neutral-400" />;
};

export default function EditorPanel({ files, activeFile, setActiveFile, onChange, streaming }) {
  const current = files.find((f) => f.name === activeFile) || files[0];

  return (
    <div className="h-full flex flex-col bg-[#080808] overflow-hidden">
      {/* Tabs */}
      <div className="flex items-stretch h-9 border-b border-forge-border overflow-x-auto shrink-0 bg-forge-surface">
        {files.length === 0 && (
          <div className="flex items-center px-4 text-xs text-neutral-500 font-mono">no files yet</div>
        )}
        {files.map((f) => (
          <button
            key={f.name}
            data-testid={`file-tab-${f.name}`}
            onClick={() => setActiveFile(f.name)}
            className={`flex items-center gap-2 px-3 text-xs font-mono whitespace-nowrap border-r border-forge-border transition-colors ${
              current?.name === f.name
                ? "bg-[#080808] border-t-2 border-t-forge-primary text-white"
                : "bg-forge-surface text-neutral-400 hover:bg-forge-hover"
            }`}
          >
            {iconFor(f.name)}
            {f.name}
          </button>
        ))}
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-auto">
        {current ? (
          <Editor
            key={current.name}
            value={current.content}
            onValueChange={(code) => onChange(current.name, code)}
            highlight={(code) => Prism.highlight(code, Prism.languages[prismLang(current.language)], prismLang(current.language))}
            padding={16}
            readOnly={streaming}
            textareaClassName="code"
            className="prism-editor min-h-full font-mono text-[13px] leading-relaxed"
            style={{ fontFamily: '"JetBrains Mono", monospace', minHeight: "100%" }}
            data-testid="code-editor"
          />
        ) : (
          <div className="h-full flex items-center justify-center text-neutral-600 text-sm font-mono">
            {"// generated code will appear here"}
          </div>
        )}
      </div>
    </div>
  );
}
